import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import {
  isCallerIDFromOrg,
  EmergencyCallsMap,
} from '@rsos/utils/isCallerIDFromOrg';

const HOVER_DEBOUNCE_MS = 300;
const SXM_ORG = 'sxm';

type HoverRoute = typeof GPX_ROUTES.CALL_QUEUE | typeof GPX_ROUTES.MAP;

interface HoverStartParams {
  callerID: string | undefined;
  payloadID: string | null;
  route: HoverRoute;
  callStartTime: number | null | undefined;
  irpVersion?: string;
  isConnectedCar?: boolean;
}

interface ActiveHover extends Omit<HoverStartParams, 'callerID'> {
  callerID: string;
  startTime: number;
}

interface CallHoverTracking {
  trackHoverStart: (params: HoverStartParams) => void;
  trackHoverEnd: () => void;
}

const CallHoverTrackingContext = createContext<CallHoverTracking | null>(null);

export function CallHoverTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const visitCounts = useRef<Map<string, number> | null>(null);
  if (visitCounts.current === null) {
    visitCounts.current = new Map();
  }
  const activeHover = useRef<ActiveHover | null>(null);

  const flushActiveHover = useCallback((): void => {
    const hover = activeHover.current;
    if (!hover) return;

    const {
      callerID,
      payloadID,
      route,
      callStartTime,
      irpVersion,
      isConnectedCar,
      startTime,
    } = hover;
    const duration = Date.now() - startTime;
    activeHover.current = null;

    if (duration < HOVER_DEBOUNCE_MS) return;

    const counts = visitCounts.current ?? new Map<string, number>();
    const visitCount = (counts.get(callerID) ?? 0) + 1;
    counts.set(callerID, visitCount);

    const irpVersionValue = irpVersion ?? null;
    const isConnectedCarValue = isConnectedCar ?? null;

    gspx.trackEvent({
      name: 'call_hover',
      type: GPX_EVENT_TYPES.HOVER,
      feature: GPX_FEATURES.CALLS,
      capability: GPX_CAPABILITIES.CALLS,
      route,
      additionalKeys: {
        visit_count: visitCount,
        hover_duration: duration,
        irp_version: irpVersionValue,
        irpVersion: irpVersionValue,
        is_connected_car: isConnectedCarValue,
        isSXMCrashed: isConnectedCarValue,
      },
      entities: {
        payload_id: payloadID ?? null,
        call_start_time: callStartTime ?? null,
      },
    });
  }, []);

  const trackHoverStart = useCallback(
    ({
      callerID,
      payloadID,
      route,
      callStartTime,
      irpVersion,
      isConnectedCar,
    }: HoverStartParams): void => {
      if (activeHover.current) flushActiveHover();
      if (!callerID) return;
      activeHover.current = {
        callerID,
        payloadID,
        route,
        callStartTime,
        irpVersion,
        isConnectedCar,
        startTime: Date.now(),
      };
    },
    [flushActiveHover],
  );

  const trackHoverEnd = useCallback((): void => {
    flushActiveHover();
  }, [flushActiveHover]);

  const value = useMemo<CallHoverTracking>(
    () => ({ trackHoverStart, trackHoverEnd }),
    [trackHoverStart, trackHoverEnd],
  );

  return (
    <CallHoverTrackingContext.Provider value={value}>
      {children}
    </CallHoverTrackingContext.Provider>
  );
}

export function useCallHoverTracking(): CallHoverTracking {
  const context = useContext(CallHoverTrackingContext);
  if (!context) {
    throw new Error(
      'useCallHoverTracking must be used within a CallHoverTrackingProvider',
    );
  }
  return context;
}

type ConnectedCarLookup = (callerID: string | undefined) => boolean;

export function useConnectedCarLookup(): ConnectedCarLookup {
  const callQueue = useSelector(
    (state: { emergencyCalls: { callQueue: EmergencyCallsMap } }) =>
      state.emergencyCalls.callQueue,
  );
  return useCallback(
    (callerID: string | undefined): boolean =>
      isCallerIDFromOrg(SXM_ORG, callQueue, callerID),
    [callQueue],
  );
}
